import {
  BOT_DIFFICULTIES,
  BOT_MODES,
  COLLISION_MODES,
  COURSE_LENGTHS,
  DIFFICULTIES,
  PARK_THEMES,
  POOL_SHAPES,
  SHORTCUT_MODES,
  SKIN_IDS,
  type GameSettings,
  type RaceResult,
  type RoomPlayer,
  type RoomState,
} from "../../shared/protocol";
import type { ActivityViewActions, ActivityViewModel } from "./types";

const SETTING_KEYS = new Set<keyof GameSettings>([
  "theme",
  "courseLength",
  "difficulty",
  "seed",
  "botMode",
  "botCount",
  "botDifficulty",
  "maxRacers",
  "collisions",
  "shortcuts",
  "powerups",
  "poolShape",
  "bestOf",
  "teams",
  "chaos",
]);

const LABELS: Record<string, string> = {
  "cloud-temple": "Cloud Temple",
  "jungle-hotel": "Jungle Hotel",
  "neon-storm": "Neon Storm",
  "sunfruit-bay": "Sunfruit Bay",
  "glacier-gulp": "Glacier Gulp",
  moonlit: "Moonlit Marina",
  relaxed: "Easygoing",
  tricky: "Tricky",
  feral: "Feral",
  short: "Quick dip",
  medium: "Grand tour",
  long: "Mega park",
  fixed: "Exact count",
  fill: "Fill empty lanes",
  off: "Off",
  light: "Bumpers",
  full: "Full contact",
  guided: "Marked routes",
  normal: "Hidden routes",
  extreme: "Anything lands",
  random: "Surprise me",
  rectangle: "Rectangle",
  square: "Square",
  circle: "Circle",
  star: "Star",
  ring: "Ring",
  crescent: "Crescent",
  tiny: "Tiny dropper",
  easy: "Easy",
  hard: "Hard",
  shark: "Shark",
  axolotl: "Axolotl",
  frog: "Frog",
  otter: "Otter",
  tiger: "Tiger",
  capybara: "Capybara",
  duck: "Duck",
  crocodile: "Crocodile",
  "red-panda": "Red Panda",
  pigeon: "Pigeon",
  sloth: "Sloth",
  unicorn: "Unicorn",
  statesman: "Statesman",
  "vip-splash": "VIP Splash",
};

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function label(value: string): string {
  return LABELS[value] ?? value.replaceAll("-", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "TB";
}

function ordinal(rank: number): string {
  const mod100 = rank % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${rank}th`;
  if (rank % 10 === 1) return `${rank}st`;
  if (rank % 10 === 2) return `${rank}nd`;
  if (rank % 10 === 3) return `${rank}rd`;
  return `${rank}th`;
}

function formatTime(milliseconds: number): string {
  const safe = Math.max(0, milliseconds);
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const millis = Math.floor(safe % 1_000);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
}

function selectOptions(values: readonly string[], selected: string, context?: string): string {
  return values
    .map(
      (value) =>
        `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(
          context === "Ride length" && value === "medium" ? "Grand tour" :
            context === "Bot brains" && value === "medium" ? "Medium" : label(value),
        )}</option>`,
    )
    .join("");
}

function avatar(player: RoomPlayer, size: "small" | "large" = "small"): string {
  const avatarClass = `player-avatar player-avatar--${size}`;
  if (player.avatarUrl) {
    return `<span class="${avatarClass}"><img src="${escapeHtml(player.avatarUrl)}" alt="" referrerpolicy="no-referrer" /></span>`;
  }
  return `<span class="${avatarClass}" aria-hidden="true">${escapeHtml(initials(player.displayName))}</span>`;
}

function skinFigure(skin: string): string {
  return `<span class="skin-figure skin-figure--${escapeHtml(skin)}" aria-hidden="true">
    <span class="skin-ears"></span><span class="skin-head"><span class="skin-face"></span></span><span class="skin-body"></span>
  </span>`;
}

function connectionText(model: ActivityViewModel): string {
  if (model.connection === "connected") return "Party online";
  if (model.connection === "reconnecting") return "Rejoining party";
  if (model.connection === "offline") return "Party offline";
  return "Connecting party";
}

export class AppView {
  readonly canvas: HTMLCanvasElement;
  private readonly screen: HTMLElement;
  private readonly statusRegion: HTMLElement;
  private model: ActivityViewModel;
  private screenKey = "";
  private toastTimer: number | null = null;
  private settingsOpen = false;
  private settingsOpener: "rider" | "rules" = "rules";
  private pendingSkinFocus: string | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly actions: ActivityViewActions,
    initialModel: ActivityViewModel,
  ) {
    this.model = initialModel;
    root.innerHTML = `
      <div class="activity-shell" data-phase="loading" data-theme="sunfruit-bay">
        <canvas class="park-canvas" role="img" tabindex="-1" aria-label="Live 3D preview of the TideBreak water park"></canvas>
        <div class="park-atmosphere" aria-hidden="true"><i></i><i></i><i></i></div>
        <header class="activity-topbar">
          <div class="brand-lockup" aria-label="TideBreak">
            <span class="brand-tide">Tide</span><span class="brand-break">Break</span>
          </div>
          <div class="party-signal" role="status">
            <span class="party-signal__dot"></span><span data-connection-label>Connecting party</span>
          </div>
          <div class="music-control">
            <button class="icon-button music-toggle" type="button" data-action="music-toggle" aria-label="Mute music">
              <span class="speaker-icon" aria-hidden="true"><i></i></span>
            </button>
            <label class="music-slider-label"><span>Music</span><input type="range" min="0" max="100" step="1" value="45" data-music-volume aria-label="Music volume" /></label>
          </div>
        </header>
        <main class="activity-screen" id="activity-screen"></main>
        <div class="toast" role="status" aria-live="polite" aria-atomic="true"></div>
        <div class="sr-status" role="status" aria-live="polite" aria-atomic="true"></div>
      </div>`;
    this.canvas = root.querySelector<HTMLCanvasElement>(".park-canvas")!;
    this.screen = root.querySelector<HTMLElement>("#activity-screen")!;
    this.statusRegion = root.querySelector<HTMLElement>(".sr-status")!;
    root.addEventListener("click", this.onClick);
    root.addEventListener("change", this.onChange);
    root.addEventListener("input", this.onInput);
    window.addEventListener("keydown", this.onKeyDown);
    root.addEventListener("focusout", this.onFocusOut);
    this.render(initialModel);
  }

  render(model: ActivityViewModel): void {
    this.model = model;
    const shell = this.root.querySelector<HTMLElement>(".activity-shell")!;
    const phase = model.error ? "error" : model.room?.phase ?? "loading";
    if (model.room && model.room.phase !== "lobby") this.settingsOpen = false;
    this.syncSettingsIsolation();
    shell.dataset.phase = phase;
    shell.dataset.theme = model.room?.settings.theme ?? "sunfruit-bay";
    this.updateHeader();

    if (model.error) {
      const key = `error:${model.error}`;
      if (key !== this.screenKey) {
        this.screenKey = key;
        this.screen.innerHTML = this.errorMarkup(model.error);
      }
      return;
    }

    if (!model.room) {
      if (this.screenKey !== "loading") {
        this.screenKey = "loading";
        this.screen.innerHTML = this.loadingMarkup();
      }
      const detail = this.screen.querySelector<HTMLElement>("[data-loading-detail]");
      if (detail) detail.textContent = model.bootstrapDetail;
      return;
    }

    if (model.room.phase === "lobby") {
      const key = `lobby:${this.settingsOpen}:${this.lobbySignature(model.room)}`;
      if (key !== this.screenKey) {
        this.screenKey = key;
        this.screen.innerHTML = this.lobbyMarkup(model.room);
      }
      if (this.pendingSkinFocus) {
        const skin = this.pendingSkinFocus;
        this.pendingSkinFocus = null;
        window.requestAnimationFrame(() => {
          this.screen.querySelector<HTMLButtonElement>(`.skin-option[data-skin='${CSS.escape(skin)}']`)?.focus();
        });
      }
      return;
    }

    if (model.room.phase === "countdown" || model.room.phase === "racing") {
      const key = `race:${model.room.phase}`;
      if (key !== this.screenKey) {
        this.screenKey = key;
        this.screen.innerHTML = this.raceMarkup(model.room);
      }
      this.updateRace(model.room);
      return;
    }

    const key = `results:${this.resultsSignature(model.room)}`;
    if (key !== this.screenKey) {
      this.screenKey = key;
      this.screen.innerHTML = this.resultsMarkup(model.room);
    }
  }

  announce(message: string): void {
    this.statusRegion.textContent = "";
    window.requestAnimationFrame(() => {
      this.statusRegion.textContent = message;
    });
  }

  toast(message: string, tone: "normal" | "error" = "normal"): void {
    const toast = this.root.querySelector<HTMLElement>(".toast")!;
    toast.textContent = message;
    toast.dataset.tone = tone;
    toast.dataset.open = "true";
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => delete toast.dataset.open, 3200);
  }

  destroy(): void {
    this.root.removeEventListener("click", this.onClick);
    this.root.removeEventListener("change", this.onChange);
    this.root.removeEventListener("input", this.onInput);
    window.removeEventListener("keydown", this.onKeyDown);
    this.root.removeEventListener("focusout", this.onFocusOut);
  }

  private updateHeader(): void {
    const labelElement = this.root.querySelector<HTMLElement>("[data-connection-label]");
    const shell = this.root.querySelector<HTMLElement>(".activity-shell")!;
    if (labelElement) labelElement.textContent = connectionText(this.model);
    shell.dataset.connection = this.model.connection;

    const slider = this.root.querySelector<HTMLInputElement>("[data-music-volume]");
    if (slider && document.activeElement !== slider) slider.value = String(Math.round(this.model.musicVolume * 100));
    const toggle = this.root.querySelector<HTMLButtonElement>("[data-action='music-toggle']");
    if (toggle) {
      const audible = this.model.musicVolume > 0;
      toggle.removeAttribute("aria-pressed");
      toggle.setAttribute("aria-label", audible ? "Mute music" : "Turn music on");
      toggle.dataset.muted = String(!audible);
    }
  }

  private loadingMarkup(): string {
    return `<section class="loading-screen" aria-labelledby="loading-title">
      <div class="loading-tower" aria-hidden="true"><i></i><i></i><i></i><b></b></div>
      <div class="loading-copy">
        <p class="loading-kicker">Park opening</p>
        <h1 id="loading-title">Waxing the slides</h1>
        <p data-loading-detail>${escapeHtml(this.model.bootstrapDetail)}</p>
        <div class="loading-track"><span></span></div>
      </div>
    </section>`;
  }

  private errorMarkup(message: string): string {
    return `<section class="error-screen" aria-labelledby="error-title">
      <div class="lifebuoy" aria-hidden="true"><i></i></div>
      <div>
        <p class="error-code">Park gate closed</p>
        <h1 id="error-title">We couldn't open this lane</h1>
        <p>${escapeHtml(message)}</p>
        <button class="button button--primary" type="button" data-action="retry">Try again</button>
        <p class="error-help">If this keeps happening, close the Activity and launch it again from your Discord voice channel.</p>
      </div>
    </section>`;
  }

  private lobbySignature(room: RoomState): string {
    return JSON.stringify({
      leaderId: room.leaderId,
      revision: room.settingsRevision,
      settings: room.settings,
      players: room.players.map((player) => [
        player.id,
        player.displayName,
        player.avatarUrl,
        player.skin,
        player.ready,
        player.connected,
        player.spectator,
      ]),
    });
  }

  private lobbyMarkup(room: RoomState): string {
    const identityId = this.model.identity?.id;
    const local = room.players.find((player) => player.id === identityId);
    const isLeader = room.leaderId === identityId;
    const humanPlayers = room.players.filter((player) => !player.isBot && !player.spectator);
    const allReady = humanPlayers.length > 0 && humanPlayers.every((player) => player.ready);
    const readyCount = humanPlayers.filter((player) => player.ready).length;
    const settings = room.settings;
    const disabled = isLeader ? "" : " disabled";
    const poolLabel = label(settings.poolShape);
    const themeLabel = label(settings.theme);
    const botSummary = settings.botMode === "off"
      ? "No bots"
      : settings.botMode === "fill"
        ? "Bots fill lanes"
        : `${settings.botCount} bot${settings.botCount === 1 ? "" : "s"}`;
    const localSkin = local?.skin ?? SKIN_IDS[0];
    const primaryAction = local?.ready && isLeader && allReady
      ? `<button class="button lobby-primary" type="button" data-action="start"><span>Start TideBreak</span><i aria-hidden="true"></i></button>`
      : `<button class="button lobby-primary" type="button" data-action="ready" aria-pressed="${local?.ready ?? false}">${local?.ready ? "Ready — waiting for crew" : "Ready up"}</button>`;

    return `<section class="lobby" aria-label="Race lobby">
      <div class="course-plaque"${this.settingsOpen ? " inert aria-hidden=\"true\"" : ""}>
        <span class="course-plaque__live"><i></i> Next ride</span>
        <h1>${escapeHtml(themeLabel)}</h1>
        <span>${escapeHtml(label(settings.difficulty))} · ${escapeHtml(
          settings.courseLength === "medium" ? "Grand tour" : label(settings.courseLength),
        )} · ${escapeHtml(poolLabel)} pool</span>
      </div>

      <aside class="crew-dock" aria-labelledby="party-title"${this.settingsOpen ? " inert aria-hidden=\"true\"" : ""}>
        <div class="crew-dock__summary">
          <div>
            <span class="eyebrow">Crew</span>
            <h2 id="party-title">${readyCount}/${humanPlayers.length} ready</h2>
          </div>
          <button class="quiet-button" type="button" data-action="invite"><span class="plus-mark" aria-hidden="true"></span>Invite</button>
        </div>
        <ol class="crew-list" aria-label="Players in this TideBreak party">
          ${humanPlayers.map((player) => this.playerChipMarkup(player, room)).join("")}
        </ol>
      </aside>

      <section class="ride-dock" aria-labelledby="ride-dock-title"${this.settingsOpen ? " inert aria-hidden=\"true\"" : ""}>
        <div class="ride-dock__rider">
          ${skinFigure(localSkin)}
          <div>
            <span class="eyebrow">Your rider</span>
            <h2 id="ride-dock-title">${escapeHtml(label(localSkin))}</h2>
          </div>
          <button class="quiet-button" type="button" data-action="settings-open" aria-label="Change rider skin">Change</button>
        </div>
        <div class="ride-summary" aria-label="Current race settings">
          <span><small>Difficulty</small><b>${escapeHtml(label(settings.difficulty))}</b></span>
          <span><small>Final drop</small><b>${escapeHtml(poolLabel)}</b></span>
          <span><small>Rivals</small><b>${escapeHtml(botSummary)}</b></span>
        </div>
        <div class="ride-dock__actions">
          <button class="quiet-button race-settings-button" type="button" data-action="settings-open"><span aria-hidden="true">⚙</span> Race settings</button>
          ${primaryAction}
          ${local?.ready && isLeader && allReady ? `<button class="ready-undo" type="button" data-action="ready">Not ready</button>` : ""}
        </div>
        <p class="ride-status">${
          local?.ready && isLeader && allReady
            ? "Crew locked in. Send it."
            : local?.ready
              ? `Waiting for ${humanPlayers.length - readyCount} rider${humanPlayers.length - readyCount === 1 ? "" : "s"}.`
              : isLeader
                ? "Ready up, then launch when your crew is in."
                : "The party leader launches when everyone is ready."
        }</p>
      </section>

      ${this.settingsOpen ? `<div class="settings-scrim" data-action="settings-close" aria-hidden="true"></div>` : ""}
      <aside class="settings-drawer" role="dialog" aria-modal="true" aria-labelledby="settings-title" aria-hidden="${!this.settingsOpen}"${this.settingsOpen ? "" : " inert"}>
        <header class="settings-drawer__header">
          <div><span class="eyebrow">TideBreak lobby</span><h2 id="settings-title">Race settings</h2></div>
          <button class="settings-close" type="button" data-action="settings-close" aria-label="Close race settings">×</button>
        </header>
        <div class="settings-scroll">
          <label class="drawer-audio"><span><b>Music</b><small>Keep voice chat clear</small></span><output>${Math.round(this.model.musicVolume * 100)}%</output><input type="range" min="0" max="100" step="1" value="${Math.round(this.model.musicVolume * 100)}" data-music-volume aria-label="Music volume" /></label>
          ${!isLeader ? `<p class="settings-locked">The party leader controls the course. You can still choose your rider.</p>` : ""}
          <fieldset class="settings-section"${disabled}>
            <legend>Course</legend>
            ${this.selectControl("Park", "theme", PARK_THEMES, settings.theme, disabled)}
            ${this.selectControl("Ride length", "courseLength", COURSE_LENGTHS, settings.courseLength, disabled)}
            ${this.selectControl("Difficulty", "difficulty", DIFFICULTIES, settings.difficulty, disabled)}
            ${this.selectControl("Final pool", "poolShape", POOL_SHAPES, settings.poolShape, disabled)}
            <button class="shuffle-course" type="button" data-action="shuffle"${disabled}><span class="shuffle-mark" aria-hidden="true"></span>Shuffle every obstacle</button>
          </fieldset>

          <fieldset class="settings-section"${disabled}>
            <legend>Racers</legend>
            ${this.selectControl("Bots", "botMode", BOT_MODES, settings.botMode, disabled)}
            <label class="control-row" data-visible="${settings.botMode !== "off"}"><span><b>Bot count</b><small>Extra pajama racers</small></span><output data-output="botCount">${settings.botCount}</output><input type="range" min="0" max="11" value="${settings.botCount}" data-setting="botCount"${disabled} /></label>
            <label class="control-row"><span><b>Park capacity</b><small>Maximum human and bot racers</small></span><output data-output="maxRacers">${settings.maxRacers}</output><input type="range" min="2" max="12" value="${settings.maxRacers}" data-setting="maxRacers"${disabled} /></label>
            ${this.selectControl("Bot brains", "botDifficulty", BOT_DIFFICULTIES, settings.botDifficulty, disabled, settings.botMode !== "off")}
          </fieldset>

          <fieldset class="settings-section"${disabled}>
            <legend>Advanced rules</legend>
            ${this.selectControl("Bumping", "collisions", COLLISION_MODES, settings.collisions, disabled)}
            ${this.selectControl("Shortcuts", "shortcuts", SHORTCUT_MODES, settings.shortcuts, disabled)}
            ${this.toggleControl("Splash boost", "Burn splash meter for a burst of speed", "powerups", settings.powerups, disabled)}
            ${this.toggleControl("Team lanes", "Paired riders cannot bump each other", "teams", settings.teams, disabled)}
            <div class="control-row"><span><b>Match</b><small>Heats in this set</small></span><div class="segmented" role="group" aria-label="Match rounds">
              ${([1, 3, 5] as const).map((bestOf) => `<button type="button" data-setting-button="bestOf" data-value="${bestOf}" aria-pressed="${settings.bestOf === bestOf}"${disabled}>${bestOf === 1 ? "Dash" : `Best of ${bestOf}`}</button>`).join("")}
            </div></div>
            <label class="control-row chaos-control"><span><b>Chaos</b><small data-chaos-copy>${["Mostly sensible", "Splashy", "Loose cannon", "Unlicensed weather"][settings.chaos] ?? "Loose cannon"}</small></span><output data-output="chaos">${settings.chaos}/3</output><input type="range" min="0" max="3" step="1" value="${settings.chaos}" data-setting="chaos"${disabled} /></label>
          </fieldset>

          <fieldset class="skin-picker settings-section">
            <legend>Your rider</legend>
            <div class="skin-strip" role="radiogroup" aria-label="Character skin">
              ${SKIN_IDS.map((skin) => `<button type="button" class="skin-option" role="radio" tabindex="${local?.skin === skin ? "0" : "-1"}" aria-checked="${local?.skin === skin}" data-action="skin" data-skin="${skin}" aria-label="${escapeHtml(label(skin))}">${skinFigure(skin)}<span>${escapeHtml(label(skin))}</span></button>`).join("")}
            </div>
          </fieldset>
        </div>
      </aside>
    </section>`;
  }

  private playerChipMarkup(player: RoomPlayer, room: RoomState): string {
    const isLocal = player.id === this.model.identity?.id;
    return `<li class="crew-chip" data-local="${isLocal}" data-ready="${player.ready}" data-disconnected="${!player.connected}">
      ${avatar(player)}
      <span><b>${escapeHtml(player.displayName)}${isLocal ? " (you)" : ""}</b><small>${player.id === room.leaderId ? "Leader" : player.ready ? "Ready" : "Not ready"}</small></span>
      <i aria-hidden="true"></i>
    </li>`;
  }

  private selectControl(
    title: string,
    key: keyof GameSettings,
    values: readonly string[],
    value: string,
    disabled: string,
    visible = true,
  ): string {
    return `<label class="control-row" data-visible="${visible}"><span><b>${escapeHtml(title)}</b></span><select data-setting="${key}"${disabled}>${selectOptions(values, value, title)}</select></label>`;
  }

  private toggleControl(
    title: string,
    description: string,
    key: keyof GameSettings,
    checked: boolean,
    disabled: string,
  ): string {
    return `<label class="control-row"><span><b>${escapeHtml(title)}</b><small>${escapeHtml(description)}</small></span><input class="switch" type="checkbox" data-setting="${key}"${checked ? " checked" : ""}${disabled} /></label>`;
  }

  private raceMarkup(room: RoomState): string {
    const local = room.players.find((player) => player.id === this.model.identity?.id);
    const leader = [...room.players]
      .filter((player) => !player.spectator)
      .sort((a, b) => b.race.progress - a.race.progress)[0];
    return `<section class="race-ui" aria-label="Race controls and status">
      <div class="race-rank"><span data-rank>${local?.race.rank ? ordinal(local.race.rank) : "—"}</span><small>of ${room.players.filter((player) => !player.spectator).length}</small></div>
      <div class="race-leader"><small>Leading</small><strong data-leader>${escapeHtml(leader?.displayName ?? "—")}</strong></div>
      <div class="round-chip">Round <b>${room.round}</b> / ${room.settings.bestOf}</div>
      <div class="race-progress" aria-label="Course progress"><div><span data-progress-fill></span></div><strong data-progress-label>0%</strong></div>
      <div class="boost-meter"><span>Boost</span><div><i data-boost-fill></i></div><b data-boost-label>0</b></div>
      <p class="control-hint" data-control-hint>A/D free aim · Space launches · Hold S to boost</p>
      <div class="touch-steer" aria-label="Steering controls">
        <button type="button" data-input="left" aria-label="Aim freely to the left"><span aria-hidden="true"></span>Left</button>
        <button type="button" data-input="right" aria-label="Aim freely to the right"><span aria-hidden="true"></span>Right</button>
      </div>
      <div class="touch-actions" aria-label="Race actions">
        <button type="button" data-input="tuck"><span>Boost</span><small>Hold / S</small></button>
        <button type="button" data-input="jump"><span>Launch</span><small>Space</small></button>
      </div>
      <div class="countdown" data-countdown aria-live="assertive"><span>Get wet</span><b data-countdown-value>3</b></div>
    </section>`;
  }

  private updateRace(room: RoomState): void {
    const local = room.players.find((player) => player.id === this.model.identity?.id);
    if (!local) return;
    const racers = room.players.filter((player) => !player.spectator);
    const sorted = [...racers].sort((a, b) => b.race.progress - a.race.progress);
    const calculatedRank = sorted.findIndex((player) => player.id === local.id) + 1;
    const rank = local.race.rank ?? calculatedRank;
    const progress = Math.round(Math.min(1, Math.max(0, local.race.progress)) * 100);
    const boost = Math.round(Math.min(100, Math.max(0, local.race.boost)));
    const finished = local.race.finishedAt !== null;
    const leader = sorted[0];
    const setText = (selector: string, value: string) => {
      const element = this.screen.querySelector<HTMLElement>(selector);
      if (element) element.textContent = value;
    };
    setText("[data-rank]", ordinal(rank));
    setText("[data-leader]", leader?.displayName ?? "—");
    setText("[data-progress-label]", finished ? "Finished" : `${progress}%`);
    setText("[data-boost-label]", String(boost));
    setText(
      "[data-control-hint]",
      finished
        ? "Finish locked — last splashes have 10 seconds"
        : local.race.offSlide
        ? "No assists — aim freely or steer back to re-enter"
        : local.race.airborne
          ? "Steer your landing"
          : "A/D free aim · Space launches · Hold S to boost",
    );
    const progressFill = this.screen.querySelector<HTMLElement>("[data-progress-fill]");
    const boostFill = this.screen.querySelector<HTMLElement>("[data-boost-fill]");
    if (progressFill) progressFill.style.width = `${progress}%`;
    if (boostFill) boostFill.style.width = `${boost}%`;
    const raceUi = this.screen.querySelector<HTMLElement>(".race-ui");
    if (raceUi) raceUi.dataset.finished = String(finished);

    const countdown = this.screen.querySelector<HTMLElement>("[data-countdown]");
    if (countdown) countdown.dataset.visible = String(room.phase === "countdown");
    if (room.phase === "countdown" && room.countdownEndsAt) {
      const seconds = Math.max(1, Math.ceil((room.countdownEndsAt - Date.now()) / 1_000));
      setText("[data-countdown-value]", String(seconds));
    }
  }

  private resultsSignature(room: RoomState): string {
    return JSON.stringify({ round: room.round, results: room.results, players: room.players.map((p) => [p.id, p.skin]) });
  }

  private resultsMarkup(room: RoomState): string {
    const localResult = room.results.find((result) => result.playerId === this.model.identity?.id);
    const localWipeout = localResult?.poolHit === false;
    const isLeader = room.leaderId === this.model.identity?.id;
    const leader = room.players.find((player) => player.id === room.leaderId);
    return `<section class="results" aria-labelledby="results-title">
      <div class="results-banner">
        <span class="results-spray" aria-hidden="true"><i></i><i></i><i></i></span>
        <p>${localWipeout ? "You missed the water" : localResult?.rank === 1 ? "You ruled the park" : "Run complete"}</p>
        <h1 id="results-title">${localWipeout ? "Wipeout" : localResult ? `<strong>${ordinal(localResult.rank)}</strong> place` : "Race results"}</h1>
        <span>${localResult ? `${localResult.totalScore.toLocaleString()} total points` : "The pool judges have spoken"}</span>
      </div>
      <div class="podium-deck">
        ${room.results.filter((result) => result.poolHit === true).slice(0, 3).map((result) => this.podiumMarkup(result, room)).join("")}
      </div>
      <div class="scoreboard">
        <div class="scoreboard__head"><h2>Final splash</h2><span>Round ${room.round} of ${room.settings.bestOf}</span></div>
        <ol>${room.results.map((result) => this.resultRowMarkup(result, room)).join("")}</ol>
      </div>
      <div class="results-actions">
        ${isLeader ? `<button class="button button--launch" type="button" data-action="rematch"><span>Race again</span><i aria-hidden="true"></i></button>` : `<p>Waiting for ${escapeHtml(leader?.displayName ?? "the party leader")} to call the rematch.</p>`}
        <button class="button button--invite" type="button" data-action="invite"><span class="plus-mark" aria-hidden="true"></span>Bring more friends</button>
      </div>
    </section>`;
  }

  private podiumMarkup(result: RaceResult, room: RoomState): string {
    const player = room.players.find((candidate) => candidate.id === result.playerId);
    if (!player) return "";
    return `<article class="podium-place podium-place--${result.rank}">
      <span class="podium-rank">${result.rank}</span>
      <div class="podium-rider">${skinFigure(player.skin)}${avatar(player, "large")}</div>
      <strong>${escapeHtml(result.displayName)}</strong>
      <small>${result.totalScore.toLocaleString()} pts</small>
    </article>`;
  }

  private resultRowMarkup(result: RaceResult, room: RoomState): string {
    const player = room.players.find((candidate) => candidate.id === result.playerId);
    const local = result.playerId === this.model.identity?.id;
    const outcome = result.poolHit === false
      ? "Wipeout"
      : result.poolHit === null
        ? `Cutoff · ${formatTime(result.finishMs)}`
        : formatTime(result.finishMs);
    return `<li data-local="${local}"><span class="result-rank">${result.rank}</span>${player ? skinFigure(player.skin) : ""}<span class="result-name"><b>${escapeHtml(result.displayName)}</b><small><span>${outcome}</span><span>${result.stuntScore.toLocaleString()} stunt</span><span>${result.dropperScore.toLocaleString()} drop</span></small></span><strong>${result.totalScore.toLocaleString()}</strong></li>`;
  }

  private readonly onClick = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("button, [data-action]");
    if (!target || target.matches(":disabled")) return;
    const action = target.dataset.action;
    if (action === "retry") this.actions.retry();
    else if (action === "invite") this.actions.invite();
    else if (action === "settings-open") {
      this.settingsOpener = target.matches(".ride-dock__rider .quiet-button") ? "rider" : "rules";
      this.settingsOpen = true;
      this.screenKey = "";
      this.render(this.model);
      window.requestAnimationFrame(() => this.screen.querySelector<HTMLButtonElement>(".settings-close")?.focus());
    } else if (action === "settings-close") {
      this.closeSettings();
    }
    else if (action === "ready") {
      const local = this.model.room?.players.find((player) => player.id === this.model.identity?.id);
      this.actions.ready(!(local?.ready ?? false), local?.skin);
    } else if (action === "start") this.actions.startRace();
    else if (action === "rematch") this.actions.rematch();
    else if (action === "music-toggle") this.actions.setMusicVolume(this.model.musicVolume > 0 ? 0 : 0.45);
    else if (action === "shuffle") this.actions.setting("seed", Math.floor(Math.random() * 2_147_483_647));
    else if (action === "skin") {
      const skin = target.dataset.skin;
      const local = this.model.room?.players.find((player) => player.id === this.model.identity?.id);
      if (skin) {
        this.pendingSkinFocus = skin;
        this.actions.ready(local?.ready ?? false, skin);
      }
    }

    const settingKey = target.dataset.settingButton;
    if (settingKey && SETTING_KEYS.has(settingKey as keyof GameSettings)) {
      this.actions.setting(settingKey as keyof GameSettings, Number(target.dataset.value) as never);
    }
  };

  private readonly onChange = (event: Event): void => {
    const input = event.target as HTMLInputElement | HTMLSelectElement | null;
    if (!input) return;
    if (input.matches("[data-music-volume]")) {
      this.actions.setMusicVolume(Number(input.value) / 100);
      const output = input.closest(".drawer-audio")?.querySelector<HTMLOutputElement>("output");
      if (output) output.value = `${input.value}%`;
      return;
    }
    const key = input.dataset.setting as keyof GameSettings | undefined;
    if (!key || !SETTING_KEYS.has(key)) return;
    let value: string | number | boolean = input.value;
    if (input instanceof HTMLInputElement && input.type === "checkbox") value = input.checked;
    else if (["botCount", "maxRacers", "seed", "bestOf", "chaos"].includes(key)) value = Number(input.value);
    this.actions.setting(key, value as never);
  };

  private readonly onInput = (event: Event): void => {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    if (input.matches("[data-music-volume]")) {
      this.actions.setMusicVolume(Number(input.value) / 100);
      const output = input.closest(".drawer-audio")?.querySelector<HTMLOutputElement>("output");
      if (output) output.value = `${input.value}%`;
      return;
    }
    const key = input.dataset.setting;
    if (key !== "botCount" && key !== "maxRacers" && key !== "chaos") return;
    const output = this.screen.querySelector<HTMLOutputElement>(`[data-output='${key}']`);
    if (output) output.value = `${input.value}${key === "chaos" ? "/3" : ""}`;
    if (key === "chaos") {
      const copy = this.screen.querySelector<HTMLElement>("[data-chaos-copy]");
      const value = Number(input.value);
      if (copy) copy.textContent = ["Mostly sensible", "Splashy", "Loose cannon", "Unlicensed weather"][value] ?? "Loose cannon";
    }
  };

  private readonly onFocusOut = (): void => {
    window.setTimeout(() => this.render(this.model), 0);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.settingsOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeSettings();
      return;
    }
    const radio = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(".skin-option[role='radio']");
    if (radio && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
      const radios = [...this.screen.querySelectorAll<HTMLButtonElement>(".skin-option[role='radio']")];
      const current = Math.max(0, radios.indexOf(radio));
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? radios.length - 1
          : (current + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) + radios.length) % radios.length;
      event.preventDefault();
      radios[nextIndex]?.click();
      return;
    }
    if (event.key !== "Tab") return;
    const drawer = this.screen.querySelector<HTMLElement>(".settings-drawer");
    if (!drawer) return;
    const focusable = [...drawer.querySelectorAll<HTMLElement>(
      "button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])",
    )].filter((element) => !element.hasAttribute("inert") && element.tabIndex >= 0);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!drawer.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  };

  private closeSettings(): void {
    this.settingsOpen = false;
    this.screenKey = "";
    this.render(this.model);
    window.requestAnimationFrame(() => {
      const selector = this.settingsOpener === "rider"
        ? ".ride-dock__rider [data-action='settings-open']"
        : ".race-settings-button";
      this.screen.querySelector<HTMLButtonElement>(selector)?.focus();
    });
  }

  private syncSettingsIsolation(): void {
    const topbar = this.root.querySelector<HTMLElement>(".activity-topbar");
    if (this.settingsOpen) {
      topbar?.setAttribute("inert", "");
      topbar?.setAttribute("aria-hidden", "true");
      this.canvas.setAttribute("aria-hidden", "true");
    } else {
      topbar?.removeAttribute("inert");
      topbar?.removeAttribute("aria-hidden");
      this.canvas.removeAttribute("aria-hidden");
    }
  }
}
